/* eslint-disable react/prop-types */
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import athenaClient from "lib/axios/clients";
import getAttach from "lib/requests/attach";
import { REACT_APP_BASE_URL } from "resources/constants";
import { setTask, removeTask } from "app/common/task/duck/slices";
import TaskProgressSnackbar from "app/common/task/TaskProgressSnackbar";
import getStatus, { PollingTimeoutError } from "lib/requests/status";
import { makeStyles } from "@material-ui/core";
import SnackbarActionButtons from "app/common/snackbar/SnackbarActionButtons";
import mixpanelTrack from "app/common/MixpanelComponent";
import { downloadBlob } from "lib/utils";

const useStyles = makeStyles(() => ({
  circularProgress: {
    color: "white",
  },
}));

const withExportScenario = (WrappedComponent, scenarioProp, template) => {
  return (props) => {
    const classes = useStyles();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [arePendingTasks, setArePendingTasks] = React.useState(false);
    const configCatSlice = useSelector((state) => state.auth.configCatUser);
    const FILETYPE = template ? "csv" : "xlsx";
    const dispatch = useDispatch();

    const preventPageUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    React.useEffect(() => {
      if (arePendingTasks)
        window.addEventListener("beforeunload", preventPageUnload);
      else window.removeEventListener("beforeunload", preventPageUnload);
      return () => {
        window.removeEventListener("beforeunload", preventPageUnload);
      };
    }, [arePendingTasks]);

    const enqueueLoadingSnackbar = (action) =>
      enqueueSnackbar(
        `'${scenarioProp.name}' is getting ready for download. You can navigate in the console while the excel is being generated`,
        {
          persist: true,
          action,
        }
      );

    const enqueueErrorSnackbar = (key) => {
      if (key) closeSnackbar(key);
      enqueueSnackbar(
        `Error when downloading '${scenarioProp.name}'. Try again shortly`,
        {
          variant: "error",
        }
      );
      setArePendingTasks(false);
    };

    const enqueueTimeoutSnackbar = (onAction, snackbarKey) => {
      if (snackbarKey) closeSnackbar(snackbarKey);
      enqueueSnackbar(
        `Could not update the status of the task. Note that if you dismiss this message, 
                    the task can still be completed, but you will not get notified`,
        {
          variant: "warning",
          persist: "true",
          action: (key) => (
            <SnackbarActionButtons
              action="Reconnect"
              onAction={onAction}
              key={key}
              onDismiss={() => setArePendingTasks(false)}
            />
          ),
        }
      );
    };

    const exportScenario = async () => {
      const config = {
        method: "GET",
        url: `${REACT_APP_BASE_URL}/scenarios/${scenarioProp.id}`,
        params: {
          file: FILETYPE,
          type: template && "template",
          cycle_type_id: scenarioProp.cycle.cycle_type_id,
          cannibalize: configCatSlice?.features.cannibalizationsOn && !template,
        },
      };
      const task = await athenaClient.request(config);

      return task.data.task;
    };

    const downloadCsv = async (status, taskId, snackbarKey) => {
      try {
        const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunk size
        const headers = {};
        let fullFile = [];

        headers.Range = `bytes=0-${CHUNK_SIZE - 1}`;
        // Request the total file size to determine the number of requests to be made
        const response = await getAttach(
          {
            path: status.task_info?.result?.path_to_s3,
            file: FILETYPE,
          },
          headers
        );
        fullFile = [response.data];
        const fileSize = parseInt(
          response.headers["content-range"].split("/")[1],
          10
        );
        const totalChunks = Math.ceil(fileSize / CHUNK_SIZE);

        // Iterate over the number of chunks and fetch each chunk with Range header
        for (let i = 1; i < totalChunks; i += 1) {
          const startByte = i * CHUNK_SIZE;
          const endByte = startByte + CHUNK_SIZE - 1;
          headers.Range = `bytes=${startByte}-${endByte}`;
          // eslint-disable-next-line no-await-in-loop
          const chunkResponse = await getAttach(
            {
              path: status.task_info?.result?.path_to_s3,
              file: FILETYPE,
            },
            headers
          );
          fullFile = fullFile.concat(chunkResponse.data);
        }
        const blobData = new Blob(fullFile);
        downloadBlob(
          blobData,
          template === "template"
            ? `${scenarioProp.name}_${scenarioProp.cycle.label}_template`
            : `${scenarioProp.name}_${scenarioProp.cycle.label}`,
          "csv"
        );
      } catch (e) {
        enqueueErrorSnackbar(snackbarKey);
      } finally {
        closeSnackbar(snackbarKey);
        dispatch(removeTask(taskId));
        setArePendingTasks(false);
      }
    };

    const handleExport = async () => {
      /* MIXPANEL TRACKS */
      mixpanelTrack(`Export scenario`);
      /* END OF MIXPANEL TRACKS */
      let snackbarKey;

      try {
        const task = await exportScenario();

        const enqueueTask = async () => {
          setArePendingTasks(true);

          snackbarKey = enqueueLoadingSnackbar(
            <TaskProgressSnackbar
              taskId={task.id}
              size={40}
              className={classes.circularProgress}
            />
          );
          try {
            await getStatus(task.id, (status) => {
              switch (status.task_status) {
                case "FAILURE":
                  enqueueErrorSnackbar(snackbarKey);
                  break;
                case "SUCCESS": {
                  downloadCsv(status, task.id, snackbarKey);
                  return;
                }
                case "PENDING":
                  return;
                default:
                  dispatch(
                    setTask({
                      id: task.id,
                      progress:
                        (status.task_info?.step * 100) /
                        status.task_info?.total_steps,
                    })
                  );
              }
            });
          } catch (e) {
            if (e instanceof PollingTimeoutError) {
              enqueueTimeoutSnackbar(enqueueTask, snackbarKey);
            } else throw e;
          }
        };

        await enqueueTask();
      } catch (e) {
        // Log the error somewhere
        enqueueErrorSnackbar(snackbarKey);
      }
    };

    return (
      <WrappedComponent
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        onClick={handleExport}
        onMouseDown={handleExport}
      />
    );
  };
};

export default withExportScenario;
